import type { TrailKind, TrailRender } from "../../../shared/shop-catalog";
import { drawCustomGrid } from "../entities/CustomArt";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  gravity: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  color2: string;
  phase: number;
  kind: TrailKind;
  active: boolean;
  /** Pixel grid for the "custom" trail kind (shared reference; never mutated). */
  cells: readonly string[] | null;
  grid: number;
}

const POOL_SIZE = 96;

export class Particles {
  private readonly pool: Particle[] = Array.from({ length: POOL_SIZE }, () => ({
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    gravity: 0,
    life: 0,
    maxLife: 0,
    size: 0,
    color: "#fff",
    color2: "#fff",
    phase: 0,
    kind: "puff" as TrailKind,
    active: false,
    cells: null,
    grid: 0,
  }));

  reset(): void {
    for (const p of this.pool) p.active = false;
  }

  emitTrail(x: number, y: number, cfg: TrailRender): void {
    const count = clusterCount(cfg.kind);
    for (let i = 0; i < count; i += 1) {
      const p = this.findFree();
      if (!p) return;
      configureParticle(p, x, y, cfg, i);
    }
  }

  emitPuff(x: number, y: number, count = 6): void {
    for (let i = 0; i < count; i += 1) {
      const p = this.findFree();
      if (!p) return;
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 40;
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed - 20;
      p.gravity = 80;
      p.maxLife = 0.4 + Math.random() * 0.3;
      p.life = p.maxLife;
      p.size = 2 + Math.random() * 2;
      p.color = "#f0e0a8";
      p.color2 = "#f0e0a8";
      p.phase = Math.random();
      p.kind = "puff";
      p.cells = null;
      p.grid = 0;
      p.active = true;
    }
  }

  update(dt: number): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      const alpha = p.life / p.maxLife;
      drawParticle(ctx, p, alpha);
    }
    ctx.globalAlpha = 1;
  }

  private findFree(): Particle | null {
    return this.pool.find((q) => !q.active) ?? null;
  }
}

function clusterCount(kind: TrailKind): number {
  if (kind === "pixel") return 3;
  if (kind === "confetti") return 2;
  if (kind === "blizzard") return 2;
  return 1;
}

function configureParticle(p: Particle, x: number, y: number, cfg: TrailRender, i: number): void {
  const k = cfg.kind;
  p.x = x + (i > 0 ? (Math.random() - 0.5) * 6 : 0);
  p.y = y + (i > 0 ? (Math.random() - 0.5) * 6 : 0);
  p.vx = -40;
  p.vy = cfg.vy;
  p.gravity = 80;
  p.maxLife = cfg.lifeMin + Math.random() * (cfg.lifeMax - cfg.lifeMin);
  p.life = p.maxLife;
  p.size = cfg.sizeMin + Math.random() * (cfg.sizeMax - cfg.sizeMin);
  p.color = cfg.color;
  p.color2 = cfg.color2 ?? cfg.color;
  p.phase = Math.random();
  p.kind = k;
  p.cells = k === "custom" ? (cfg.cells ?? null) : null;
  p.grid = cfg.grid ?? 0;
  p.active = true;

  if (k === "bubble") {
    p.gravity = -20;
    p.vx = -20 + (Math.random() - 0.5) * 8;
  } else if (k === "ghost" || k === "smoke" || k === "vapor" || k === "shadow") {
    p.gravity = -10;
    p.vx = -20;
  } else if (k === "laser") {
    p.gravity = 0;
    p.vx = -120;
    p.vy = 0;
  } else if (k === "lightning") {
    p.gravity = 0;
    p.vx = -30;
  } else if (k === "portal") {
    p.gravity = 0;
    p.vx = -30;
    p.phase = i / 6;
  } else if (k === "confetti") {
    p.gravity = 50;
    p.vx = -30 + (Math.random() - 0.5) * 40;
    p.vy = -20 + Math.random() * -15;
  } else if (k === "blizzard") {
    p.gravity = 20;
    p.vx = -30 + (Math.random() - 0.5) * 20;
  } else if (k === "cosmic" || k === "blossom" || k === "magic") {
    p.gravity = 20;
  } else if (k === "divine") {
    p.gravity = -15;
    p.vx = -25;
  } else if (k === "creator") {
    // Flat, no drift: particles stack into a straight, thick horizontal band.
    p.gravity = 0;
    p.vy = 0;
    p.vx = -60;
  }
}

function drawParticle(ctx: CanvasRenderingContext2D, p: Particle, alpha: number): void {
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  switch (p.kind) {
    case "puff":
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
      return;
    case "rainbow": {
      const hue = (p.phase * 360 + (1 - alpha) * 60) % 360;
      ctx.fillStyle = `hsl(${hue}, 85%, 60%)`;
      drawCircle(ctx, p.x, p.y, p.size);
      return;
    }
    case "sparkle": {
      const tw = 0.5 + Math.abs(Math.sin(p.life * 12 + p.phase * 6)) * 0.6;
      ctx.fillStyle = p.color;
      drawStar4(ctx, p.x, p.y, p.size * tw);
      return;
    }
    case "fire": {
      const t = 1 - alpha;
      ctx.fillStyle = mixHex(p.color2, p.color, t);
      drawFlame(ctx, p.x, p.y, p.size * (0.8 + alpha * 0.5));
      return;
    }
    case "neon":
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.5;
      drawCircleStroke(ctx, p.x, p.y, p.size * (1 + (1 - alpha) * 0.6));
      return;
    case "lightning":
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.5;
      drawBolt(ctx, p.x, p.y, p.size * 2, p.phase);
      return;
    case "bubble":
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1;
      drawCircleStroke(ctx, p.x, p.y, p.size);
      ctx.fillStyle = "#ffffff";
      ctx.globalAlpha = alpha * 0.6;
      drawCircle(ctx, p.x - p.size * 0.4, p.y - p.size * 0.4, p.size * 0.25);
      return;
    case "smoke": {
      const grown = p.size * (1 + (1 - alpha) * 1.2);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha * 0.7;
      drawCircle(ctx, p.x, p.y, grown);
      return;
    }
    case "star":
      ctx.fillStyle = p.color;
      drawStar5(ctx, p.x, p.y, p.size * 1.3, p.phase * Math.PI * 2);
      return;
    case "ghost":
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha * 0.5;
      drawWisp(ctx, p.x, p.y, p.size * 1.4);
      return;
    case "pixel":
      ctx.fillStyle = p.color;
      ctx.fillRect(Math.round(p.x) - 1, Math.round(p.y) - 1, 2, 2);
      return;
    case "plasma": {
      const t = (Math.sin(p.life * 8 + p.phase * 6) + 1) / 2;
      ctx.fillStyle = mixHex(p.color, p.color2, t);
      drawCircle(ctx, p.x, p.y, p.size);
      return;
    }
    case "cosmic":
      ctx.fillStyle = p.color2;
      drawStar4(ctx, p.x, p.y, p.size * 1.2);
      ctx.fillStyle = p.color;
      drawCircle(ctx, p.x, p.y, p.size * 0.4);
      return;
    case "heart":
      ctx.fillStyle = p.color;
      drawHeart(ctx, p.x, p.y, p.size * 1.4);
      return;
    case "cyber":
      ctx.fillStyle = p.color;
      ctx.fillRect(Math.round(p.x) - p.size / 2, Math.round(p.y) - 0.5, p.size, 1);
      ctx.fillRect(Math.round(p.x) - 0.5, Math.round(p.y) - p.size / 2, 1, p.size);
      return;
    case "blizzard":
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 0.8;
      drawSnowflake(ctx, p.x, p.y, p.size, p.phase);
      return;
    case "shadow":
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha * 0.55;
      drawCircle(ctx, p.x, p.y, p.size * (1 + (1 - alpha) * 0.5));
      return;
    case "laser":
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x + p.size * 2, p.y);
      ctx.stroke();
      return;
    case "confetti": {
      const hue = (p.phase * 360) % 360;
      ctx.fillStyle = `hsl(${hue}, 85%, 60%)`;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.phase * Math.PI * 2 + p.life * 6);
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
      return;
    }
    case "magic": {
      const t = (Math.sin(p.life * 10 + p.phase * 4) + 1) / 2;
      ctx.fillStyle = mixHex(p.color, p.color2, t);
      drawStar4(ctx, p.x, p.y, p.size * 1.3);
      return;
    }
    case "void":
      ctx.fillStyle = p.color;
      drawCircle(ctx, p.x, p.y, p.size);
      ctx.strokeStyle = p.color2;
      ctx.lineWidth = 1;
      drawCircleStroke(ctx, p.x, p.y, p.size * 1.3);
      return;
    case "gold":
      ctx.fillStyle = p.color;
      drawCircle(ctx, p.x, p.y, p.size);
      ctx.fillStyle = p.color2;
      drawCircle(ctx, p.x - p.size * 0.3, p.y - p.size * 0.3, p.size * 0.35);
      return;
    case "candy":
      ctx.fillStyle = p.color;
      drawCircle(ctx, p.x, p.y, p.size);
      ctx.strokeStyle = p.color2;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p.x - p.size, p.y);
      ctx.lineTo(p.x + p.size, p.y);
      ctx.stroke();
      return;
    case "portal": {
      const t = 1 - alpha;
      const angle = p.phase * Math.PI * 2 + p.life * 8;
      const r = p.size * (1 - t * 0.5);
      const px = p.x + Math.cos(angle) * 4;
      const py = p.y + Math.sin(angle) * 4;
      ctx.fillStyle = t < 0.5 ? p.color : p.color2;
      drawCircle(ctx, px, py, r);
      return;
    }
    case "crystal":
      ctx.fillStyle = p.color;
      drawDiamond(ctx, p.x, p.y, p.size * 1.2);
      ctx.strokeStyle = p.color2;
      ctx.lineWidth = 0.8;
      drawDiamondStroke(ctx, p.x, p.y, p.size * 1.2);
      return;
    case "glitch": {
      const swap = Math.floor(p.life * 30) % 2 === 0;
      ctx.fillStyle = swap ? p.color : p.color2;
      const jx = (Math.random() - 0.5) * 2;
      ctx.fillRect(p.x + jx - p.size / 2, p.y - p.size / 2, p.size, p.size);
      return;
    }
    case "aurora": {
      const t = (Math.sin(p.life * 4 + p.phase * 6) + 1) / 2;
      ctx.fillStyle = mixHex(p.color, p.color2, t);
      ctx.globalAlpha = alpha * 0.6;
      ctx.fillRect(p.x - p.size, p.y - p.size / 3, p.size * 2, p.size / 1.5);
      return;
    }
    case "blossom":
      ctx.fillStyle = p.color;
      drawPetal(ctx, p.x, p.y, p.size * 1.3, p.phase * Math.PI * 2 + p.life * 3);
      return;
    case "vapor":
      ctx.fillStyle = mixHex(p.color, p.color2, p.phase);
      ctx.globalAlpha = alpha * 0.6;
      drawWisp(ctx, p.x, p.y, p.size * 1.3);
      return;
    case "divine": {
      const pulse = 0.6 + Math.abs(Math.sin(p.life * 10 + p.phase * 6)) * 0.4;
      ctx.fillStyle = p.color2;
      ctx.globalAlpha = alpha * 0.5;
      drawCircle(ctx, p.x, p.y, p.size * 1.8 * pulse);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha;
      drawStar4(ctx, p.x, p.y, p.size * pulse);
      return;
    }
    case "creator": {
      // Thick rainbow band: hue follows screen-x so overlapping circles read as
      // one smooth gradient line. Kept near-opaque so it looks solid, fading
      // only at the very tail end of life.
      const hue = (((p.x * 4 - performance.now() / 6) % 360) + 360) % 360;
      ctx.globalAlpha = Math.min(1, alpha * 4);
      ctx.fillStyle = `hsl(${hue}, 90%, 55%)`;
      drawCircle(ctx, p.x, p.y, p.size);
      return;
    }
    case "custom": {
      if (!p.cells || p.grid <= 0) return;
      const s = p.size * 2.6;
      drawCustomGrid(ctx, p.cells, p.grid, p.x - s / 2, p.y - s / 2, s, s);
      return;
    }
  }
}

// --- shape primitives ---

function drawCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function drawCircleStroke(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
}

function drawStar4(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.3, y - r * 0.3);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x + r * 0.3, y + r * 0.3);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.3, y + r * 0.3);
  ctx.lineTo(x - r, y);
  ctx.lineTo(x - r * 0.3, y - r * 0.3);
  ctx.closePath();
  ctx.fill();
}

function drawStar5(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  rot: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i += 1) {
    const angle = rot + (i * Math.PI) / 5 - Math.PI / 2;
    const radius = i % 2 === 0 ? r : r * 0.45;
    const px = x + Math.cos(angle) * radius;
    const py = y + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
}

function drawSnowflake(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  rot: number,
): void {
  for (let i = 0; i < 3; i += 1) {
    const a = rot * Math.PI + i * (Math.PI / 3);
    ctx.beginPath();
    ctx.moveTo(x - Math.cos(a) * r, y - Math.sin(a) * r);
    ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    ctx.stroke();
  }
}

function drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y + r * 0.4);
  ctx.bezierCurveTo(x + r, y - r * 0.4, x + r * 0.6, y - r, x, y - r * 0.3);
  ctx.bezierCurveTo(x - r * 0.6, y - r, x - r, y - r * 0.4, x, y + r * 0.4);
  ctx.closePath();
  ctx.fill();
}

function drawDiamond(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.7, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.7, y);
  ctx.closePath();
  ctx.fill();
}

function drawDiamondStroke(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.7, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.7, y);
  ctx.closePath();
  ctx.stroke();
}

function drawFlame(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.quadraticCurveTo(x + r * 0.7, y, x + r * 0.4, y + r * 0.5);
  ctx.quadraticCurveTo(x, y + r, x - r * 0.4, y + r * 0.5);
  ctx.quadraticCurveTo(x - r * 0.7, y, x, y - r);
  ctx.fill();
}

function drawWisp(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.6, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawBolt(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  len: number,
  phase: number,
): void {
  const seed = phase * 100;
  ctx.beginPath();
  let cx = x;
  let cy = y;
  ctx.moveTo(cx, cy);
  for (let i = 1; i <= 3; i += 1) {
    cx -= len / 3;
    cy += (((seed + i * 17) % 7) - 3) * 1.2;
    ctx.lineTo(cx, cy);
  }
  ctx.stroke();
}

function drawPetal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  rot: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.45, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function mixHex(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * t);
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * t);
  const bl = Math.round(ca[2] + (cb[2] - ca[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

function parseHex(hex: string): [number, number, number] | null {
  if (!hex.startsWith("#") || (hex.length !== 7 && hex.length !== 4)) return null;
  if (hex.length === 4) {
    return [
      parseInt(hex[1]! + hex[1]!, 16),
      parseInt(hex[2]! + hex[2]!, 16),
      parseInt(hex[3]! + hex[3]!, 16),
    ];
  }
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}
